/**
 * Presentational status pill. Maps backend status/owner strings to a tone.
 * Backend values are NOT renamed — only displayed (optionally relabeled).
 */
const tones = {
  neutral: 'bg-slate-100 text-slate-600 ring-slate-200',
  info: 'bg-sky-50 text-sky-700 ring-sky-200',
  cyan: 'bg-cyan-50 text-cyan-700 ring-cyan-200',
  success: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  warning: 'bg-amber-50 text-amber-700 ring-amber-200',
  danger: 'bg-rose-50 text-rose-700 ring-rose-200',
};

/** Best-effort mapping of known backend status values to a tone. */
const STATUS_TONE = {
  // package_status
  DRAFT: 'neutral',
  SUBMITTED: 'info',
  CLASSIFIED: 'cyan',
  CERTIFIED: 'success',
  // next_action_owner
  originator: 'warning',
  custodian: 'info',
  // exception severity
  CRITICAL: 'danger',
  HIGH: 'danger',
  MEDIUM: 'warning',
  LOW: 'info',
  // exception status
  OPEN: 'warning',
  IN_CURE: 'info',
  CURED: 'success',
  // asset request status
  CREATED: 'info',
  ACKNOWLEDGED: 'cyan',
  COMPLETED: 'success',
  REJECTED: 'danger',
  AUTHORITATIVE: 'success',
  ACCEPTED: 'success',
  COMPLETE: 'success',
  ORIGINATOR: 'warning',
  INGEST: 'info',
  SEALED: 'success',
  FAILED: 'danger',
  INCOMPLETE_PENDING_REPAIR: 'warning',
  CANCELLED: 'neutral',
  // provider review / pipeline
  UNASSIGNED: 'warning',
  ASSIGNED: 'info',
  IN_REVIEW: 'cyan',
  ROUTED: 'info',
  PENDING: 'warning',
  APPROVED: 'success',
  CANDIDATE: 'cyan',
  REPOSITORY_ONLY: 'neutral',
  RETURN: 'warning',
  BLOCK: 'danger',
  PROVIDER: 'warning',
  CUSTODIAN: 'info',
  AWAITING_PROVIDER_REVIEW: 'warning',
  AWAITING_CUSTODIAN: 'info',
  AWAITING_ADMISSION: 'info',
  READY_TO_ROUTE: 'cyan',
  VIEW: 'neutral',
  HEALTHY: 'success',
  DEGRADED: 'warning',
  DOWN: 'danger',
  EXCEPTION: 'danger',
  CONNECTOR: 'warning',
  // originator admission readiness / checks
  NOT_EVALUATED: 'neutral',
  EVALUATING: 'info',
  READY_TO_CERTIFY: 'success',
  REVIEW_REQUIRED: 'warning',
  BLOCKED: 'danger',
  RETURNED: 'warning',
  READY: 'success',
  LINKED: 'info',
  REVIEW_NEEDED: 'warning',
};

export function StatusPill({ value, tone, label, className = '' }) {
  if (value === undefined || value === null || value === '') return null;
  const resolvedTone = tone || STATUS_TONE[value] || 'neutral';
  const toneClass = tones[resolvedTone] || tones.neutral;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${toneClass} ${className}`.trim()}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" aria-hidden />
      {label ?? value}
    </span>
  );
}
